import React, { useState } from "react";
import { useTheme } from "../../components/ThemeContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, CheckCircle, Shield, User, Mail, Loader2, XCircle } from "lucide-react";
import AxonMetricHeader from "../../components/layout/AxonMetricHeader.jsx";
import apiService from "../../services/api";

const SignupPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  // Credentials state
  const [credentials, setCredentials] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  // UI State
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    if (!credentials.firstName || !credentials.lastName || !credentials.username || !credentials.email || !credentials.password) {
      setError("Please fill in all fields.");
      setIsLoading(false);
      return;
    }

    if (credentials.password !== credentials.confirmPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    if (!agreeToTerms) {
      setError("Please agree to the Terms and Conditions.");
      setIsLoading(false);
      return;
    }

    try {
      const userData = {
        firstName: credentials.firstName.trim(),
        lastName: credentials.lastName.trim(),
        username: credentials.username.trim(),
        email: credentials.email.trim(),
        password: credentials.password,
        role: 'user',
      };

      const response = await apiService.signup(userData);

      if (response.success) {
        if (response.requiresVerification && response.email) {
          setSuccess("Verification code sent! Redirecting...");
          setTimeout(() => {
            navigate('/verify-otp', { state: { email: response.email } });
          }, 1500);
        } else {
          setSuccess("Account created successfully! Redirecting...");
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        }
      } else {
        setError(response.error || "Signup failed.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      backgroundImage: "url('/assets/login_bg.jpg')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      minHeight: "100vh",
      position: "relative"
    }}>
      {isDarkMode && (
        <div style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(11, 17, 32, 0.50)",
          zIndex: 0,
          pointerEvents: "none"
        }} />
      )}
      {/* Enhanced CSS Styles matching Login Page */}
      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .brand-glass {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.15) 50%, 
            rgba(0, 132, 207, 0.1) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .gradient-border {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 25%, 
            #1C4D88 50%, 
            #0084CF 75%, 
            #0084CF 100%
          );
          background-size: 300% 300%;
          animation: gradientShift 8s ease infinite;
          padding: 2px;
          border-radius: 20px;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px -12px rgba(0, 132, 207, 0.25);
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .inner-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 18px;
        }

        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-image: url('/assets/login_bg.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: -2;
        }

        .background-overlay {
          display: none;
        }

        .professional-button {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 50%, 
            #1C4D88 100%
          );
          transition: all 0.3s ease;
        }

        .professional-button:hover {
          background: linear-gradient(135deg, 
            #0066A3 0%, 
            #0066A3 50%, 
            #68d391 100%
          );
          transform: translateY(-1px);
          box-shadow: 0 12px 30px rgba(0, 132, 207, 0.4);
        }

        input::placeholder {
          font-weight: 500 !important;
          color: rgba(100, 116, 139, 0.7) !important;
        }
      `}</style>

      {/* Background elements */}
      <div className="background-container"></div>
      <div className="background-overlay"></div>

      <div className="h-screen w-full flex flex-col relative overflow-hidden" style={{ position: "relative", zIndex: 1 }}>
        {/* Fixed Header */}
        <AxonMetricHeader />

        {/* Main Content - Vertically centered, strictly no page scroll */}
        <main className="relative z-10 flex-1 w-full flex items-center justify-center px-4 pt-16 pb-3 overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full max-w-5xl mx-auto"
          >
            <div className="gradient-border">
              <div className="inner-content flex flex-col lg:flex-row rounded-2xl overflow-hidden shadow-2xl">

                {/* Left Panel - Branding & Highlights */}
                <section className="hidden lg:flex flex-col justify-center w-2/5 p-6 lg:p-7 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F2F8FC]/90 via-[#F2F8FC]/80 to-[#F2F8FC]/70"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#0084CF]/20 via-[#3EA3DA]/15 to-[#3EA3DA]/10 rounded-full blur-2xl floating-animation"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-br from-[#C2DFF1]/30 via-[#C2DFF1]/25 to-[#0084CF]/15 rounded-full blur-2xl"></div>

                  <div className="relative z-10">
                    <motion.div
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="mb-5"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2.5 bg-gradient-to-br from-[#0084CF]/20 via-[#C2DFF1]/40 to-[#C2DFF1]/30 rounded-xl shadow-sm border border-[#0084CF]/20 backdrop-blur-sm">
                          <Shield size={22} className="text-[#0084CF]" />
                        </div>
                        <div>
                          <h2 className="text-xl font-black bg-gradient-to-r from-[#0084CF] via-[#0066A3] to-[#0066A3] bg-clip-text text-transparent">
                            Join AxonMetric
                          </h2>
                          <p className="text-xs text-[#1C4D88] font-bold">Digitalization of Renewable Assets</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 font-bold leading-relaxed">
                        Advanced real-time analytics platform for comprehensive solar and wind generation monitoring.
                      </p>
                    </motion.div>

                    <div className="space-y-3">
                      {[
                        "Real-time Performance Monitoring",
                        "Daily Generational Reports (DGR)",
                        "Multi-Site Fleet Management",
                        "Microsoft Authenticator (2FA) Protection"
                      ].map((feature, index) => (
                        <motion.div
                          key={feature}
                          initial={{ opacity: 0, x: -15 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.08, duration: 0.35 }}
                          className="flex items-center space-x-3 group"
                        >
                          <div className="w-6 h-6 bg-gradient-to-r from-[#0084CF]/30 via-[#8AC4E6]/40 to-[#8AC4E6]/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm border border-[#0084CF]/30 flex-shrink-0">
                            <CheckCircle size={12} className="text-[#0084CF]" />
                          </div>
                          <span className="text-xs text-gray-900 font-black group-hover:text-[#0084CF] transition-colors duration-200">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Right Panel - Compact Redesigned Form */}
                <section className="w-full lg:w-3/5 p-5 lg:p-7 flex flex-col justify-center relative">
                  <div className="w-full max-w-lg mx-auto">
                    <div className="text-center mb-3">
                      <h1 className="text-2xl lg:text-3xl font-black bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent">
                        Create Your Account
                      </h1>
                      <p className="text-xs text-gray-700 font-extrabold mt-0.5">
                        Enter your details to register for AxonMetric
                      </p>
                    </div>

                    <form onSubmit={handleSignup} className="space-y-2.5">
                      {/* Row 1: First Name & Last Name */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <User size={12} className="text-[#0084CF]" /> First Name
                          </label>
                          <input
                            type="text"
                            placeholder="John"
                            value={credentials.firstName}
                            onChange={e => handleInputChange('firstName', e.target.value)}
                            className="w-full px-3 py-2 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                            disabled={isLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <User size={12} className="text-[#0084CF]" /> Last Name
                          </label>
                          <input
                            type="text"
                            placeholder="Doe"
                            value={credentials.lastName}
                            onChange={e => handleInputChange('lastName', e.target.value)}
                            className="w-full px-3 py-2 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                            disabled={isLoading}
                          />
                        </div>
                      </div>

                      {/* Row 2: Username & Email */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <User size={12} className="text-[#0084CF]" /> Username
                          </label>
                          <input
                            type="text"
                            placeholder="johndoe"
                            value={credentials.username}
                            onChange={e => handleInputChange('username', e.target.value)}
                            className="w-full px-3 py-2 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                            disabled={isLoading}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <Mail size={12} className="text-[#0084CF]" /> Email Address
                          </label>
                          <input
                            type="email"
                            placeholder="john@company.com"
                            value={credentials.email}
                            onChange={e => handleInputChange('email', e.target.value)}
                            className="w-full px-3 py-2 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                            disabled={isLoading}
                          />
                        </div>
                      </div>

                      {/* Row 3: Password & Confirm Password */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <Lock size={12} className="text-[#0084CF]" /> Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              value={credentials.password}
                              onChange={e => handleInputChange('password', e.target.value)}
                              className="w-full px-3 py-2 pr-9 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                              style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                              disabled={isLoading}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0084CF]"
                            >
                              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-black text-gray-800 mb-1 flex items-center gap-1">
                            <Lock size={12} className="text-[#0084CF]" /> Confirm Password
                          </label>
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={credentials.confirmPassword}
                            onChange={e => handleInputChange('confirmPassword', e.target.value)}
                            className="w-full px-3 py-2 brand-glass border border-[#0084CF]/30 rounded-xl focus:outline-none transition-all font-bold hover:border-[#0084CF] focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-black text-sm"
                            style={{ color: '#000000', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                            disabled={isLoading}
                          />
                        </div>
                      </div>

                      {/* Row 4: Terms Checkbox */}
                      <div className="flex items-center gap-2 pt-0.5">
                        <input
                          type="checkbox"
                          id="agreeTerms"
                          checked={agreeToTerms}
                          onChange={e => setAgreeToTerms(e.target.checked)}
                          className="w-4 h-4 rounded text-[#0066A3] focus:ring-[#0084CF] cursor-pointer"
                        />
                        <label htmlFor="agreeTerms" className="text-xs text-gray-800 font-extrabold cursor-pointer select-none">
                          I agree to the <Link to="/terms-and-conditions" target="_blank" className="text-[#0084CF] hover:underline font-bold">Terms and Conditions</Link>
                        </label>
                      </div>

                      {/* Error & Success Messages */}
                      {error && (
                        <div className="p-2 bg-red-50 text-red-600 rounded-lg text-xs font-bold flex items-center gap-2 border border-red-200">
                          <XCircle size={14} className="flex-shrink-0" />
                          <span>{error}</span>
                        </div>
                      )}

                      {success && (
                        <div className="p-2 bg-green-50 text-green-600 rounded-lg text-xs font-bold flex items-center gap-2 border border-green-200">
                          <CheckCircle size={14} className="flex-shrink-0" />
                          <span>{success}</span>
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-2.5 professional-button text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Creating Account...</span>
                          </>
                        ) : (
                          <span>Create Account</span>
                        )}
                      </button>

                      {/* Login link */}
                      <p className="text-center text-xs text-gray-800 font-extrabold mt-1.5">
                        Already have an account?{" "}
                        <Link to="/login" className="text-[#0084CF] hover:text-[#0066A3] hover:underline font-black">
                          Login here
                        </Link>
                      </p>
                    </form>
                  </div>
                </section>

              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default SignupPage;
